import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Shield,
  Sparkles,
  ArrowRight,
  Calendar,
  Filter,
  RefreshCw,
} from 'lucide-react';
import {
  useDashboardStats,
  useScreeningSummary,
  useRecentActivity,
  useRefreshDashboard
} from '../hooks/useDashboard';
import { useTestMode } from '../contexts/TestModeContext';
import { useSettings } from '../hooks/useSettings';
import GettingStarted from './GettingStarted';
import WelcomeModal from './WelcomeModal';

// Import Hyperkit-styled components
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { StatCard, StatCardSkeleton } from './ui/stat-card';
import { ScreeningBar, ScreeningBarSkeleton } from './ui/screening-bar';
import { ActivityItem, ActivityItemSkeleton } from './ui/activity-item';

// AI Insights remain mock data for now (separate AI integration)
const mockAIInsights = [
  {
    type: 'efficiency',
    title: 'Processing Speed Up',
    description: 'Average review time decreased by 23% this week. AI pre-screening is identifying 67% of clear approvals.',
    action: 'View Analytics'
  },
  {
    type: 'risk',
    title: 'Geographic Risk Cluster',
    description: '4 applicants from high-risk jurisdiction detected in the last hour. Consider enhanced due diligence.',
    action: 'Review Applicants'
  },
  {
    type: 'compliance',
    title: 'OFAC List Updated',
    description: 'New sanctions list version (2025-11-27) available. 3 existing applicants require re-screening.',
    action: 'Run Re-screen'
  }
];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

// Format relative time for activity feed
function formatRelativeTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

// Build KPI cards from API data
function buildKpiCards(stats) {
  return [
    {
      label: "Today's Applicants",
      value: stats?.today_applicants ?? 0,
      change: stats?.today_applicants_change ?? 0,
      changeLabel: 'vs yesterday',
      icon: Users
    },
    {
      label: 'Approved',
      value: stats?.approved ?? 0,
      change: stats?.approved_change ?? 0,
      changeLabel: 'vs yesterday',
      icon: CheckCircle2,
      color: 'success'
    },
    {
      label: 'Rejected',
      value: stats?.rejected ?? 0,
      change: stats?.rejected_change ?? 0,
      changeLabel: 'vs yesterday',
      icon: XCircle,
      color: 'danger'
    },
    {
      label: 'Pending Review',
      value: stats?.pending_review ?? 0,
      change: stats?.pending_review_change ?? 0,
      changeLabel: stats?.pending_review_change === 0 ? 'no change' : 'vs yesterday',
      icon: Clock,
      color: 'warning'
    }
  ];
}

// Build screening hits from API data
function buildScreeningHits(screening) {
  return [
    { severity: 'high', count: screening?.sanctions_matches ?? 0, label: 'Sanctions Matches' },
    { severity: 'medium', count: screening?.pep_hits ?? 0, label: 'PEP Hits' },
    { severity: 'low', count: screening?.adverse_media ?? 0, label: 'Adverse Media' }
  ];
}

// Insight type badge colors
const insightTypeStyles = {
  efficiency: 'bg-green-500/15 text-green-500',
  risk: 'bg-orange-500/15 text-orange-500',
  compliance: 'bg-teal-500/15 text-teal-500',
};

// Filter dropdown component
function FilterDropdown({ label, icon: Icon, options, value, onChange, isOpen, onToggle }) {
  return (
    <div className="relative">
      <Button
        variant={isOpen ? 'secondary' : 'ghost'}
        size="sm"
        onClick={onToggle}
        className="gap-2"
      >
        <Icon size={16} />
        {options.find(o => o.value === value)?.label || label}
      </Button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-700 rounded-lg shadow-elevation-lg p-1.5 min-w-[140px] z-50">
          {options.map((option) => (
            <div
              key={option.value}
              className={`px-3 py-2 rounded-md text-sm cursor-pointer transition-colors ${
                value === option.value
                  ? 'bg-teal-500/10 text-teal-500'
                  : 'text-grey-600 dark:text-grey-400 hover:bg-grey-100 dark:hover:bg-grey-800'
              }`}
              onClick={() => { onChange(option.value); onToggle(); }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Error state component
function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-grey-500 gap-3">
      <AlertTriangle size={24} />
      <p className="text-sm">{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} className="gap-2">
          <RefreshCw size={14} />
          Retry
        </Button>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { data: settings } = useSettings();

  // Welcome modal state - show once on first login
  const [showWelcome, setShowWelcome] = useState(
    () => localStorage.getItem('gc_welcome_seen') !== 'true'
  );

  // Derive user role from Auth0 claims
  const userRole = user?.['https://getclearance.dev/role'] || user?.role || 'admin';
  const tenantName = typeof settings?.general?.company_name === 'object'
    ? settings?.general?.company_name?.value
    : settings?.general?.company_name;

  const handleCloseWelcome = () => {
    setShowWelcome(false);
  };

  // Onboarding state - show unless dismissed
  const [showOnboarding, setShowOnboarding] = useState(
    () => localStorage.getItem('gc_onboarding_dismissed') !== 'true'
  );

  const handleDismissOnboarding = () => {
    localStorage.setItem('gc_onboarding_dismissed', 'true');
    setShowOnboarding(false);
  };

  // Filter state
  const [dateFilter, setDateFilter] = useState('today');
  const [productFilter, setProductFilter] = useState('all');
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [showProductMenu, setShowProductMenu] = useState(false);

  // Test mode context — pass is_sandbox to all dashboard API calls
  const { isTestMode } = useTestMode();

  // Fetch dashboard data (filtered by test/live mode)
  const { data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = useDashboardStats({ is_sandbox: isTestMode });
  const { data: screening, isLoading: screeningLoading, isError: screeningError, refetch: refetchScreening } = useScreeningSummary({ is_sandbox: isTestMode });
  const { data: activity, isLoading: activityLoading, isError: activityError, refetch: refetchActivity } = useRecentActivity(20, { is_sandbox: isTestMode });
  const refreshAll = useRefreshDashboard();

  // Handle AI insight actions
  const handleInsightAction = (insight) => {
    switch (insight.action) {
      case 'View Analytics':
        navigate('/analytics');
        break;
      case 'Review Applicants':
        navigate('/applicants?risk_level=high');
        break;
      case 'Run Re-screen':
        navigate('/screening?needs_rescreen=true');
        break;
      default:
        break;
    }
  };

  // Handle activity item click
  const handleActivityClick = (item) => {
    switch (item.type) {
      case 'approved':
      case 'rejected':
      case 'resubmission':
        if (item.applicant_id) {
          navigate(`/applicants/${item.applicant_id}`);
        }
        break;
      case 'screening_hit':
        if (item.hit_id) {
          navigate(`/screening?hit_id=${item.hit_id}`);
        } else if (item.applicant_id) {
          navigate(`/applicants/${item.applicant_id}?tab=screening`);
        }
        break;
      case 'document_uploaded':
        if (item.applicant_id) {
          navigate(`/applicants/${item.applicant_id}?tab=documents`);
        }
        break;
      default:
        break;
    }
  };

  // Date filter options
  const dateFilterOptions = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
  ];

  // Product filter options
  const productFilterOptions = [
    { value: 'all', label: 'All Products' },
    { value: 'kyc', label: 'KYC' },
    { value: 'kyb', label: 'KYB' },
    { value: 'aml', label: 'AML Screening' },
  ];

  // Derive display data from API response
  const kpiCards = buildKpiCards(stats);
  const screeningHits = buildScreeningHits(screening);
  const activityItems = activity?.items ?? [];

  // Last updated timestamp
  const lastUpdated = stats ? new Date().toLocaleTimeString() : null;

  return (
    <div className="max-w-[1400px]" data-testid="page-dashboard" data-page-ready={!statsLoading} data-page-loading={statsLoading}>
      {/* Welcome Modal - shows once on first login */}
      {showWelcome && (
        <WelcomeModal
          onClose={handleCloseWelcome}
          tenantName={tenantName}
          userRole={userRole}
        />
      )}

      {showOnboarding && (
        <GettingStarted onDismiss={handleDismissOnboarding} />
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-display-xxs text-grey-900 dark:text-grey-50">{getGreeting()}</h1>
          <p className="text-body-sm text-grey-500 dark:text-grey-400 mt-1">
            Here's what's happening with your compliance pipeline
          </p>
          {lastUpdated && (
            <span className="text-body-xs text-grey-400 dark:text-grey-500 flex items-center gap-1 mt-1">
              Last updated: {lastUpdated}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshAll}
            disabled={statsLoading}
            className="gap-2"
          >
            <RefreshCw size={14} className={statsLoading ? 'animate-spin' : ''} />
            Refresh
          </Button>

          <FilterDropdown
            label="Today"
            icon={Calendar}
            options={dateFilterOptions}
            value={dateFilter}
            onChange={setDateFilter}
            isOpen={showDateMenu}
            onToggle={() => { setShowDateMenu(!showDateMenu); setShowProductMenu(false); }}
          />

          <FilterDropdown
            label="All Products"
            icon={Filter}
            options={productFilterOptions}
            value={productFilter}
            onChange={setProductFilter}
            isOpen={showProductMenu}
            onToggle={() => { setShowProductMenu(!showProductMenu); setShowDateMenu(false); }}
          />
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {statsLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : statsError ? (
          <Card className="col-span-4">
            <ErrorState message="Failed to load stats" onRetry={refetchStats} />
          </Card>
        ) : (
          kpiCards.map((kpi, idx) => (
            <StatCard
              key={idx}
              label={kpi.label}
              value={kpi.value}
              change={kpi.change}
              changeLabel={kpi.changeLabel}
              icon={kpi.icon}
              color={kpi.color}
            />
          ))
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column (2/3) */}
        <div className="col-span-2 space-y-6">
          {/* AI Insights Card */}
          <Card className="border-teal-500/30 bg-gradient-to-br from-grey-50 to-teal-50/50 dark:from-grey-900 dark:to-teal-950/30 dark:border-teal-500/50">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Sparkles size={18} className="text-teal-500" />
                AI Insights
              </CardTitle>
              <span
                className="text-body-sm text-teal-500 cursor-pointer hover:underline flex items-center gap-1"
                onClick={() => navigate('/analytics')}
              >
                View All <ArrowRight size={14} />
              </span>
            </CardHeader>
            <CardContent className="space-y-3">
              {mockAIInsights.map((insight, idx) => (
                <div key={idx} className="p-4 bg-white/70 dark:bg-grey-800/80 rounded-lg border border-grey-200/60 dark:border-transparent">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${insightTypeStyles[insight.type]}`}>
                      {insight.type}
                    </span>
                    <span className="text-body-sm font-semibold text-grey-900 dark:text-grey-50">{insight.title}</span>
                  </div>
                  <p className="text-body-xs text-grey-600 dark:text-grey-400 mb-3 leading-relaxed">{insight.description}</p>
                  <span
                    className="text-body-sm text-teal-600 dark:text-teal-400 font-medium cursor-pointer hover:underline inline-flex items-center gap-1"
                    onClick={() => handleInsightAction(insight)}
                  >
                    {insight.action} <ArrowRight size={12} />
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Recent Activity Card */}
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Recent Activity</CardTitle>
              <span
                className="text-body-sm text-teal-500 cursor-pointer hover:underline flex items-center gap-1"
                onClick={() => navigate('/audit-log')}
              >
                View All <ArrowRight size={14} />
              </span>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <>
                  <ActivityItemSkeleton />
                  <ActivityItemSkeleton />
                  <ActivityItemSkeleton />
                  <ActivityItemSkeleton />
                </>
              ) : activityError ? (
                <ErrorState message="Failed to load activity" onRetry={refetchActivity} />
              ) : activityItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-grey-500 gap-2">
                  <Clock size={24} />
                  <p className="text-sm">No recent activity</p>
                </div>
              ) : (
                activityItems.map((item, idx) => (
                  <ActivityItem
                    key={idx}
                    type={item.type}
                    title={item.applicant_name}
                    detail={
                      item.type === 'approved' ? `Approved by ${item.reviewer || 'System'}` :
                      item.type === 'rejected' ? (item.detail || `Rejected by ${item.reviewer || 'System'}`) :
                      item.detail
                    }
                    time={formatRelativeTime(item.time)}
                    onClick={() => handleActivityClick(item)}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column (1/3) */}
        <div className="space-y-6">
          {/* Screening Hits Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield size={16} />
                Screening Hits
              </CardTitle>
            </CardHeader>
            <CardContent>
              {screeningLoading ? (
                <>
                  <ScreeningBarSkeleton />
                  <ScreeningBarSkeleton />
                  <ScreeningBarSkeleton />
                </>
              ) : screeningError ? (
                <ErrorState message="Failed to load screening data" onRetry={refetchScreening} />
              ) : (
                screeningHits.map((hit, idx) => (
                  <ScreeningBar
                    key={idx}
                    severity={hit.severity}
                    label={hit.label}
                    count={hit.count}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* SLA Performance Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock size={16} />
                SLA Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* SLA Ring */}
              <div className="w-[120px] h-[120px] mx-auto mb-4 relative">
                <svg width="120" height="120" viewBox="0 0 120 120" className="-rotate-90">
                  <circle
                    cx="60" cy="60" r="54"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                    className="text-grey-200 dark:text-grey-700"
                  />
                  <circle
                    cx="60" cy="60" r="54"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray="339.3"
                    strokeDashoffset="33.93"
                    className="text-green-500"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-display-xxs text-grey-900 dark:text-grey-50">94%</span>
                  <span className="text-body-xs text-grey-500">On Time</span>
                </div>
              </div>

              {/* SLA Stats */}
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="text-heading-lg text-grey-900 dark:text-grey-50">2.4h</div>
                  <div className="text-body-xs text-grey-500">Avg Review Time</div>
                </div>
                <div>
                  <div className="text-heading-lg text-grey-900 dark:text-grey-50">3</div>
                  <div className="text-body-xs text-grey-500">At Risk</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
